import { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { FaRegCreditCard, FaMoneyBillWave, FaRegCalendar } from 'react-icons/fa';
import { LuTicket } from 'react-icons/lu';
import { DateTime } from 'luxon';
import { Formik } from 'formik';

import FORMATTERS from 'helpers/formatters';
import { useMediaQuery } from 'helpers';
import { FormDateFromToField, PageHeader } from '_components/_core';

import {
  ChargesQuantityCard,
  ChargesTotalCard,
  ChargesAverageTicketCard,
  ChargesStatusList,
  ChargesPaymentMethodList,
} from './components';

import { StyledFormSelectField } from '../ZenpaperDashboard/styles';

function ChargesDashboard({
  user,
  chargesDashboard,
  isLoading,
  onFetchChargesDashboard,
  activeCompany,
}) {
  const { isMobile, isDesktopExtraLarge } = useMediaQuery();

  useEffect(() => {
    const oldValues = JSON.parse(localStorage.getItem('chargesDashboard'));

    if (oldValues) {
      onFetchChargesDashboard(oldValues);
    } else {
      onFetchChargesDashboard();
    }
  }, [onFetchChargesDashboard, activeCompany]);

  const renderDesktopExtraLargeView = () => (
    <>
      <Row>
        <Col sm={4}>
          <ChargesQuantityCard data={chargesDashboard} isLoading={isLoading} />
        </Col>
        <Col sm={4}>
          <ChargesTotalCard data={chargesDashboard} isLoading={isLoading} />
        </Col>
        <Col sm={4}>
          <ChargesAverageTicketCard data={chargesDashboard} isLoading={isLoading} />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col sm={6}>
          <ChargesStatusList data={chargesDashboard} isLoading={isLoading} />
        </Col>
        <Col sm={6}>
          <ChargesPaymentMethodList data={chargesDashboard} isLoading={isLoading} />
        </Col>
      </Row>
    </>
  );

  const renderDesktopView = () => (
    <>
      <Row>
        <Col sm={4}>
          <ChargesQuantityCard data={chargesDashboard} isLoading={isLoading} />
        </Col>
        <Col sm={4}>
          <ChargesTotalCard data={chargesDashboard} isLoading={isLoading} />
        </Col>
        <Col sm={4}>
          <ChargesAverageTicketCard data={chargesDashboard} isLoading={isLoading} />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col sm={6}>
          <ChargesStatusList data={chargesDashboard} isLoading={isLoading} />
        </Col>
        <Col sm={6}>
          <ChargesPaymentMethodList data={chargesDashboard} isLoading={isLoading} />
        </Col>
      </Row>
    </>
  );

  const renderMobileView = () => (
    <>
      <Row>
        <Col sm={12}>
          <ChargesQuantityCard
            data={chargesDashboard}
            isLoading={isLoading}
            icon={<FaRegCreditCard />}
          />
        </Col>
        <Col sm={12} className="mt-3">
          <ChargesTotalCard
            data={chargesDashboard}
            isLoading={isLoading}
            icon={<FaMoneyBillWave />}
          />
        </Col>
        <Col sm={12} className="mt-3">
          <ChargesAverageTicketCard
            data={chargesDashboard}
            isLoading={isLoading}
            icon={<LuTicket />}
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col sm={12}>
          <ChargesStatusList data={chargesDashboard} isLoading={isLoading} />
        </Col>
        <Col sm={12} className="mt-3">
          <ChargesPaymentMethodList data={chargesDashboard} isLoading={isLoading} />
        </Col>
      </Row>
    </>
  );

  const storeInLocalStorage = useCallback(values => {
    localStorage.setItem('chargesDashboard', JSON.stringify(values));
  }, []);

  const initialValues = useMemo(() => {
    const oldValues = JSON.parse(localStorage.getItem('chargesDashboard'));

    if (oldValues) {
      return oldValues;
    }

    return {
      start_date: DateTime.now().startOf('month').toFormat('yyyy-MM-dd HH:mm:ss'),
      end_date: DateTime.now().endOf('month').toFormat('yyyy-MM-dd HH:mm:ss'),
      quick_date: 'thisMonth',
      date_type: 'due_date',
    };
  }, []);

  const handleChargesSubmit = useCallback(
    values => {
      onFetchChargesDashboard(values);

      storeInLocalStorage(values);
    },
    [onFetchChargesDashboard, storeInLocalStorage],
  );

  const handleQuickDateChange = useCallback(
    (quickDate, _values, setFieldValue, submitForm) => {
      const now = DateTime.now();

      const format = date => date.toFormat('yyyy-MM-dd HH:mm:ss');

      switch (quickDate) {
        case 'today':
          setFieldValue('start_date', format(now.startOf('day')));
          setFieldValue('end_date', format(now.endOf('day')));
          break;
        case 'thisMonth':
          setFieldValue('start_date', format(now.startOf('month')));
          setFieldValue('end_date', format(now.endOf('month')));
          break;
        case 'thisYear':
          setFieldValue('start_date', format(now.startOf('year')));
          setFieldValue('end_date', format(now.endOf('year')));
          break;
        case 'allTime':
          setFieldValue('start_date', format(now.startOf('year')));
          setFieldValue('end_date', format(now.endOf('year')));
          break;
        case 'custom':
          setFieldValue('start_date', format(now.startOf('month')));
          setFieldValue('end_date', format(now.endOf('month')));
          break;
        default:
          break;
      }

      setFieldValue('quick_date', quickDate);

      if (quickDate !== 'custom') {
        submitForm();
      }
    },
    [],
  );

  return (
    <>
      <Row>
        <Col className="pl-4 pr-4">
          <PageHeader
            title="Dashboard"
            description={`Olá, ${FORMATTERS.DISPLAY_NAME(user)}`}
            className="mb-0 ml-2"
            sideContent={
              <Formik
                initialValues={initialValues}
                onSubmit={handleChargesSubmit}
                enableReinitialize
              >
                {({ setFieldValue, values, submitForm }) => (
                  <div className="d-flex align-items-center">
                    <StyledFormSelectField
                      name="quick_date"
                      placeholder="Período"
                      options={[
                        {
                          value: 'today',
                          label: 'Hoje',
                          icon: <FaRegCalendar />,
                        },
                        {
                          value: 'thisMonth',
                          label: 'Este Mês',
                          icon: <FaRegCalendar />,
                        },
                        {
                          value: 'thisYear',
                          label: 'Este Ano',
                          icon: <FaRegCalendar />,
                        },
                        {
                          value: 'allTime',
                          label: 'Desde o início',
                          icon: <FaRegCalendar />,
                        },
                        {
                          value: 'custom',
                          label: 'Personalizado',
                          icon: <FaRegCalendar />,
                        },
                      ]}
                      onChange={option =>
                        handleQuickDateChange(
                          option.value,
                          values,
                          setFieldValue,
                          submitForm,
                        )
                      }
                      isSearchable={false}
                      width="150px"
                    />
                    {values.quick_date === 'custom' && (
                      <FormDateFromToField
                        label={null}
                        fromName="start_date"
                        fromPlaceholder="De:"
                        fromId="start_date"
                        toName="end_date"
                        toPlaceholder="Até:"
                        toId="end_date"
                        inputContainerClassName="mt-1"
                        highlightFilled
                        fromStyle={{
                          borderLeft: 'none',
                          textAlign: 'center',
                        }}
                        toStyle={{
                          textAlign: 'center',
                        }}
                      />
                    )}
                    {/* <StyledFormSelectField
											name="date_type"
											placeholder="Tipo de data"
											options={[
												{ value: "due_date", label: "Data de Vencimento" },
												{ value: "paid_at", label: "Data de Pagamento" },
												{ value: "created_at", label: "Data de Criação" },
											]}
											isSearchable={false}
										/> */}
                    {/* <Button
											className="ml-3"
											variant="primary"
											onClick={handleSubmit}
										>
											Filtrar
										</Button> */}
                  </div>
                )}
              </Formik>
            }
          />
        </Col>
      </Row>
      <hr />
      {!isDesktopExtraLarge && !isMobile && renderDesktopView()}
      {isDesktopExtraLarge && renderDesktopExtraLargeView()}
      {isMobile && renderMobileView()}
    </>
  );
}

ChargesDashboard.defaultProps = {
  activeCompany: null,
  chargesDashboard: {
    charges_count: 0,
    charges_total: 0,
    charges_average_ticket: 0,
    charges_status: {},
    charges_payment_method: {},
    charges_summary: {},
  },
  isLoading: false,
};

ChargesDashboard.propTypes = {
  activeCompany: PropTypes.object,
  chargesDashboard: PropTypes.object,
  isLoading: PropTypes.bool,
  onFetchChargesDashboard: PropTypes.func.isRequired,
};

export default ChargesDashboard;
